import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface CurrentClients {
  gym: number;
  group: number;
}

export function SimpleHomePage() {
  const [currentClients, setCurrentClients] = useState<CurrentClients | null>(null);

  // Fetch current clients count
  useEffect(() => {
    const fetchCurrentClients = async () => {
      try {
        const response = await fetch('/api/current-clients');
        if (response.ok) {
          const data = await response.json();
          setCurrentClients({ gym: data.gym, group: data.group });
        }
      } catch (error) {
        console.error('Error fetching current clients:', error);
      }
    };

    fetchCurrentClients();
    const interval = setInterval(fetchCurrentClients, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen theme-main-content">
      {/* Desktop Sidebar */}
      <div className="hidden">
        <div className="flex items-center flex-shrink-0 px-6">
          <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center mr-3">
            <span className="text-white font-bold text-sm">LSC</span>
          </div>
          <span className="text-white font-semibold">Level Sport</span>
        </div>

        <nav className="mt-5 flex-1 px-2 space-y-1">
          <Link to="/" className="text-white bg-gray-800 group flex items-center px-2 py-2 text-sm font-medium rounded-md">
            <span className="mr-3">🏠</span>
            Главная
          </Link>
          <Link to="/profile" className="text-gray-300 hover:bg-gray-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md">
            <span className="mr-3">👤</span>
            Личный кабинет
          </Link>
          <Link to="/group-lessons" className="text-gray-300 hover:bg-gray-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md">
            <span className="mr-3">🏃‍♀️</span>
            Группы
          </Link>
          <Link to="/personal-training" className="text-gray-300 hover:bg-gray-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md">
            <span className="mr-3">💪</span>
            Персональные
          </Link>
          <Link to="/courts" className="text-gray-300 hover:bg-gray-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md">
            <span className="mr-3">🎾</span>
            Корты
          </Link>
          <Link to="/beauty" className="text-gray-300 hover:bg-gray-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md">
            <span className="mr-3">💄</span>
            Beauty
          </Link>
          <Link to="/restaurant" className="text-gray-300 hover:bg-gray-700 hover:text-white group flex items-center px-2 py-2 text-sm font-medium rounded-md">
            <span className="mr-3">🍽️</span>
            Ресторан
          </Link>
        </nav>

        {/* Status */}
        <div className="px-4 py-3 border-t border-gray-700">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span className="text-green-400 text-xs">Приложение работает!</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-h-screen flex items-center">
        <main className="w-full p-6">
          {/* Current Clients Ticker - Бегущая строка */}
          {currentClients && (
            <div className="mb-6 w-full overflow-hidden">
              <div className="relative py-2">
                <div className="ticker-content whitespace-nowrap inline-block">
                  <span className="mx-6 inline-flex items-center space-x-2 text-gray-700 dark:text-gray-200 font-semibold text-lg">
                    <span>🏋️</span>
                    <span>Зал: {currentClients.gym}</span>
                  </span>
                  <span className="mx-6 inline-flex items-center space-x-2 text-gray-700 dark:text-gray-200 font-semibold text-lg">
                    <span>👯</span>
                    <span>Группы: {currentClients.group}</span>
                  </span>
                </div>
              </div>
              <style>{`
                @keyframes ticker {
                  0% { transform: translateX(100vw); }
                  100% { transform: translateX(-100%); }
                }
                .ticker-content {
                  animation: ticker 20s linear infinite;
                }
              `}</style>
            </div>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 max-w-6xl mx-auto">
            <Link to="/profile" className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-gray-900 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">👤</div>
              <div className="font-semibold text-lg">Личный кабинет</div>
              <div className="text-sm opacity-75">Мой кабинет</div>
            </Link>

            <Link to="/group-lessons" className="bg-gradient-to-r from-blue-500 to-blue-600 text-gray-900 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">🏃‍♀️</div>
              <div className="font-semibold text-lg">Группы</div>
              <div className="text-sm opacity-75">47 занятий</div>
            </Link>

            <Link to="/personal-training" className="bg-gradient-to-r from-orange-500 to-orange-600 text-gray-900 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">💪</div>
              <div className="font-semibold text-lg">Персональные</div>
              <div className="text-sm opacity-75">49 тренеров</div>
            </Link>

            <Link to="/courts" className="bg-gradient-to-r from-green-500 to-green-600 text-gray-900 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">🎾</div>
              <div className="font-semibold text-lg">Корты</div>
              <div className="text-sm opacity-75">Теннис + Падел</div>
            </Link>

            <Link to="/beauty" className="bg-gradient-to-r from-pink-500 to-pink-600 text-gray-900 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">💆</div>
              <div className="font-semibold text-lg">Beauty & SPA</div>
              <div className="text-sm opacity-75">Массаж, косметология</div>
            </Link>

            <Link to="/restaurant" className="bg-gradient-to-r from-purple-500 to-purple-600 text-gray-900 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">🍽️</div>
              <div className="font-semibold text-lg">Ресторан</div>
              <div className="text-sm opacity-75">Меню и заказы</div>
            </Link>

            <Link to="/club-cards" className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">💳</div>
              <div className="font-semibold text-lg">Клубные карты</div>
              <div className="text-sm opacity-75">Абонементы и карты</div>
            </Link>

            <Link to="/ai-assistant" className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-gray-900 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <div className="text-3xl mb-3">🤖</div>
              <div className="font-semibold text-lg">Бот</div>
              <div className="text-sm opacity-75">AI помощник</div>
            </Link>
          </div>

        </main>
      </div>
    </div>
  );
}